import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supportAPI } from '../../services/api';
import './SupportPage.css';

interface SupportTicket {
  category: string;
  subject: string;
  message: string;
}

const CATEGORIES = [
  { value: '', label: 'Select a category' },
  { value: 'loan_query', label: 'Loan Query' },
  { value: 'payment_issue', label: 'Payment Issue' },
  { value: 'account_problem', label: 'Account Problem' },
  { value: 'disbursement', label: 'Disbursement Delay' },
  { value: 'nsfas', label: 'NSFAS Related' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'other', label: 'Other' },
];

const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<SupportTicket>({
    category: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!ticket.category) newErrors.category = 'Please select a category';
    if (!ticket.subject.trim()) newErrors.subject = 'Please enter a subject';
    if (!ticket.message.trim()) newErrors.message = 'Please describe your issue';
    if (ticket.message.trim().length < 20) newErrors.message = 'Please provide more detail (at least 20 characters)';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await supportAPI.submitTicket({
        category: ticket.category,
        subject: ticket.subject.trim(),
        message: ticket.message.trim(),
      });
      setReference(result.data?.reference || null);
      setSubmitted(true);
    } catch (err: any) {
      const data = err?.response?.data;
      const firstDetail = Array.isArray(data?.details) ? data.details[0]?.msg : undefined;
      setSubmitError(
        firstDetail ||
          data?.message ||
          data?.error ||
          'Failed to submit your request. Please try again or email us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof SupportTicket, value: string) => {
    setTicket(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (submitted) {
    return (
      <div className="support-page">
        <div className="support-header">
          <button className="support-back-btn" onClick={() => navigate('/dashboard')} type="button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1>Need Help?</h1>
        </div>

        <div className="support-success">
          <div className="success-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#2ECC40" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h2>Request Submitted</h2>
          <p>
            We've received your query and will get back to you within <strong>24 hours</strong> via 
            email ({user?.email || 'your registered email'}) or SMS.
          </p>
          <div className="success-ref">
            Reference: #{reference || 'pending'}
          </div>
          <div className="success-actions">
            <button className="support-btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
            <button
              className="support-btn-secondary"
              onClick={() => {
                setSubmitted(false);
                setReference(null);
                setSubmitError(null);
                setTicket({ category: '', subject: '', message: '' });
              }}
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="support-header">
        <button className="support-back-btn" onClick={() => navigate('/dashboard')} type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1>Need Help?</h1>
      </div>

      {/* Quick Links */}
      <div className="support-quick-links">
        <button className="quick-link-card" onClick={() => navigate('/faq')}>
          <div className="quick-link-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="quick-link-text">
            <span className="quick-link-title">FAQ</span>
            <span className="quick-link-desc">Find answers to common questions</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <a className="quick-link-card" href="https://wa.me/27722734008" target="_blank" rel="noopener noreferrer">
          <div className="quick-link-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </div>
          <div className="quick-link-text">
            <span className="quick-link-title">WhatsApp</span>
            <span className="quick-link-desc">072 273 4008</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </a>

        <a className="quick-link-card" href="mailto:info@genlend.co.za">
          <div className="quick-link-icon email">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div className="quick-link-text">
            <span className="quick-link-title">Email Us</span>
            <span className="quick-link-desc">info@genlend.co.za</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </a>
      </div>

      {/* Ticket Form */}
      <div className="support-form-section">
        <h2>Submit a Query or Complaint</h2>
        <p className="form-subtitle">Describe your issue below and we'll respond within 24 hours.</p>

        {submitError && (
          <div
            role="alert"
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              marginBottom: '1rem',
            }}
          >
            {submitError}
          </div>
        )}

        <form className="support-form" onSubmit={handleSubmit}>
          <div className="support-form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              value={ticket.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className={errors.category ? 'error' : ''}
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {errors.category && <span className="field-error">{errors.category}</span>}
          </div>

          <div className="support-form-group">
            <label htmlFor="subject">Subject *</label>
            <input
              id="subject"
              type="text"
              placeholder="Brief summary of your issue"
              value={ticket.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              className={errors.subject ? 'error' : ''}
              maxLength={100}
            />
            {errors.subject && <span className="field-error">{errors.subject}</span>}
          </div>

          <div className="support-form-group">
            <label htmlFor="message">Describe your issue *</label>
            <textarea
              id="message"
              placeholder="Please provide as much detail as possible so we can help you quickly..."
              value={ticket.message}
              onChange={(e) => handleChange('message', e.target.value)}
              className={errors.message ? 'error' : ''}
              rows={5}
              maxLength={1000}
            />
            <div className="char-count">{ticket.message.length}/1000</div>
            {errors.message && <span className="field-error">{errors.message}</span>}
          </div>

          <button
            type="submit"
            className="support-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportPage;
